import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const EMPTY = { productType: 'GOOD', sku: '', name: '', listPrice: '0', reorderPoint: 0, unitOfMeasure: 'EACH', tracking: 'NONE' };

// Common units offered in the quick-create; codes stay stable (EACH, KG, …),
// labels are translated so users never see raw codes like "EACH".
// Services (websites, consulting …) are billed per hour/day/flat - they never
// carry stock, so the stock fields disappear for them.
const GOOD_UNITS = ['EACH', 'BOX', 'KG', 'L', 'M', 'HOUR'];
const SERVICE_UNITS = ['HOUR', 'DAY', 'FLAT'];

export function ProductCreateModal({ open, onClose, onCreated }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  // Tracks the last auto-generated SKU so we only overwrite the field as long
  // as the user has not typed their own number.
  const autoSku = useRef('');

  const isService = form.productType === 'SERVICE';
  const units = isService ? SERVICE_UNITS : GOOD_UNITS;

  const nextSku = useQuery({
    queryKey: ['inventory', 'next-sku', form.productType],
    queryFn: () => inventoryApi.nextProductSku(form.productType).then((r) => r.items?.sku || r.items),
    enabled: open,
  });

  useEffect(() => {
    const sku = typeof nextSku.data === 'string' ? nextSku.data : nextSku.data?.sku;
    if (!open || !sku) return;
    setForm((prev) => {
      if (prev.sku && prev.sku !== autoSku.current) return prev; // user typed their own
      autoSku.current = sku;
      return { ...prev, sku };
    });
  }, [open, nextSku.data]);

  const create = useMutation({
    mutationFn: (body) => inventoryApi.createProduct(body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'next-sku'] });
      autoSku.current = '';
      setForm(EMPTY);
      setError(null);
      onClose();
      // Inline-create flows (e.g. quote lines) pick the fresh product up directly.
      onCreated?.(created);
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function handleField(key) {
    return (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  }

  function handleTypeChange(event) {
    const productType = event.target.value;
    // Switching type resets the stock-only fields and picks a sensible unit.
    setForm((prev) => ({
      ...prev,
      productType,
      unitOfMeasure: productType === 'SERVICE' ? 'HOUR' : 'EACH',
      reorderPoint: 0,
      tracking: 'NONE',
    }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    create.mutate({
      productType: form.productType,
      sku: form.sku.trim(),
      name: form.name.trim(),
      unitOfMeasure: form.unitOfMeasure.trim() || (isService ? 'HOUR' : 'EACH'),
      listPrice: form.listPrice || '0',
      reorderPoint: isService ? 0 : Number(form.reorderPoint) || 0,
      tracking: isService ? 'NONE' : form.tracking,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('inv.products.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="modal-body" style={{ padding: 0, gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('inv.products.modal.type')}</span>
          <select className="select" value={form.productType} onChange={handleTypeChange}>
            <option value="GOOD">{t('inv.products.modal.typeGood')}</option>
            <option value="SERVICE">{t('inv.products.modal.typeService')}</option>
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('inv.products.col.sku')}</span>
            <input
              className="input"
              value={form.sku}
              onChange={handleField('sku')}
              placeholder={isService ? t('inv.products.modal.skuHintService') : t('inv.products.modal.skuHint')}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">{t('col.unit')}</span>
            <select className="select" value={form.unitOfMeasure} onChange={handleField('unitOfMeasure')}>
              {units.map((u) => (
                <option key={u} value={u}>{t(`uom.unit.${u}`)}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('col.name')}</span>
          <input className="input" value={form.name} onChange={handleField('name')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('inv.products.modal.listPrice')}</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={form.listPrice}
              onChange={handleField('listPrice')}
            />
          </label>
          {!isService && (
            <label className="field">
              <span className="field-label">{t('inv.products.modal.reorderPoint')}</span>
              <input
                className="input"
                type="number"
                min="0"
                step="1"
                value={form.reorderPoint}
                onChange={handleField('reorderPoint')}
              />
            </label>
          )}
        </div>
        {!isService && (
          <label className="field">
            <span className="field-label">{t('inv.track.tracking')}</span>
            <select className="input" value={form.tracking} onChange={handleField('tracking')}>
              <option value="NONE">{t('inv.track.NONE')}</option>
              <option value="BATCH">{t('inv.track.BATCH')}</option>
              <option value="SERIAL">{t('inv.track.SERIAL')}</option>
            </select>
          </label>
        )}
        {isService && <p className="muted text-sm" style={{ margin: 0 }}>{t('inv.products.modal.serviceHint')}</p>}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
